import type Phaser from "phaser";
import type { EffectsDetail } from "daedalus-renderer-phaser";
import type { CharacterFigure } from "../character.ts";
import type { FigureVisit } from "./puddle-feet.ts";
import { dripGrowthThatFits, paintDripInto } from "./drip-paint.ts";

/**
 * The drip under a Drenched character (ADR-144's amendment of 2026-11-01): a few drops fall from its hem to its feet and
 * end in a small splash, more of them the wetter it is, and a character that stands still long enough grows a small puddle
 * under its feet in the rain's puddle colours (`paintDripInto`), which stays behind to dry when it walks off. The
 * drips and their puddles are the page's alone: nothing is sent, saved or collectable. Effects Reduced halves the drops;
 * Off and reduced motion draw no drops, and the puddles, which have no motion of their own, still grow and dry. A puddle
 * never crosses a wall, a prop or a table (`blockedAt`). Everything is pooled.
 */

/** The time between two drops at the wettest, in ms; the gap grows as the character dries. */
export const DRIP_INTERVAL_MS = 240;
/** How long a drop falls, in ms. */
export const DRIP_FALL_MS = 230;
/** How long a drop's splash shows, fading, in ms. */
export const DRIP_SPLASH_MS = 220;
/** How opaque a drop's splash is when it lands. */
export const DRIP_SPLASH_ALPHA = 0.7;
/** How long a character stands still before its drips start a puddle, in ms. */
export const DRIP_PUDDLE_ONSET_MS = 8000;
/** How long a drip puddle takes from its start to its full size, in ms. */
export const DRIP_PUDDLE_GROW_MS = 25000;
/** How long a full drip puddle takes to dry once nothing drips on it, in ms. */
export const DRIP_PUDDLE_DRY_MS = 90000;
/** The drip puddles one character leaves at once; a new one takes the place of its oldest. */
export const DRIP_PUDDLES_PER_FIGURE = 2;
/** The drip puddles drawn at once on a page; past it no new puddle starts until one dries. */
export const DRIP_PUDDLES_PER_VIEW = 12;
/** The fill a drip puddle stops growing at, for the wettest character: a small puddle, never a pond. */
export const DRIP_PUDDLE_MAX_FILL = 0.75;

/**
 * Reads `?drenched=` of a development page, the mockup's force: how wet the player's own character is, from 0 to 1.
 *
 * @param raw The parameter's value, or null.
 * @returns The level, or null for none or a value that is not a number from 0 to 1.
 */
export function parseForcedDrenched(raw: string | null): number | null {
  if (raw === null || raw === "") return null;
  const level = Number(raw);
  return Number.isFinite(level) && level > 0 && level <= 1 ? level : null;
}

/** How long another character's drip takes to fade once its Drenched ends, in ms (only its owner knows its time left). */
export const DRIP_FADE_MS = 4000;

/**
 * Rain falls nowhere, and nothing blocks a puddle.
 *
 * @returns False.
 */
const NEVER_RAINING = (): boolean => false;

const DROP_KEY = "weather:drip";
const SPLASH_KEY = "weather:drip-splash";
const DROP_POOL = 24;
/** A drip puddle canvas side in world pixels: room for `DRIP_PUDDLE_PX`, its lobes and its halo. */
const SLOT_PX = 32;
const FILL_STEPS = 24;
const STILL_PX = 0.5;

/** One falling drop and its splash. */
interface Drop {
  readonly image: Phaser.GameObjects.Image;
  readonly splash: Phaser.GameObjects.Image;
  x: number;
  top: number;
  bottom: number;
  startedAt: number;
  live: boolean;
}

/** One drip puddle: its shape, its canvas and how far it has grown or dried. */
interface DripPuddle {
  readonly texture: Phaser.Textures.CanvasTexture;
  readonly image: Phaser.GameObjects.Image;
  readonly pixels: ImageData;
  live: boolean;
  serial: number;
  x: number;
  y: number;
  owner: number;
  fill: number;
  step: number;
  feeding: boolean;
  most: number;
}

/** What the view keeps of a figure between frames. */
interface Track {
  x: number;
  y: number;
  stillMs: number;
  nextDrop: number;
  puddle: DripPuddle | null;
  seen: boolean;
  wet: number;
  noRoom: boolean;
}

/** What the drip view reads. */
export interface DripViewOptions {
  readonly scene: Phaser.Scene;
  readonly groundDepth: number;
  readonly figures: (visit: FigureVisit) => void;
  readonly level: (id: number) => number;
  readonly rainingOn?: (x: number, y: number) => boolean;
  readonly blockedAt?: (x: number, y: number) => boolean;
}

/**
 * The drips of every Drenched figure drawn.
 */
export class DripView {
  private readonly scene: Phaser.Scene;
  private readonly groundDepth: number;
  private readonly figures: (visit: FigureVisit) => void;
  private readonly level: (id: number) => number;
  private readonly rainingOn: (x: number, y: number) => boolean;
  private readonly blockedAt: (x: number, y: number) => boolean;
  private readonly tracks = new Map<number, Track>();
  private readonly drops: Drop[] = [];
  private readonly puddles: DripPuddle[] = [];
  private nextDrop = 0;
  private serial = 0;
  private now = 0;
  private lastNow = -1;
  private dt = 0;
  private detail: EffectsDetail = "full";

  /**
   * @param options The scene, the depth puddles draw at, the figures, how wet each is (0 dry to 1 drenched) and whether rain
   *   falls on a point, where the rain hides the drip.
   */
  public constructor(options: DripViewOptions) {
    this.scene = options.scene;
    this.groundDepth = options.groundDepth;
    this.figures = options.figures;
    this.level = options.level;
    this.rainingOn = options.rainingOn ?? NEVER_RAINING;
    this.blockedAt = options.blockedAt ?? NEVER_RAINING;
    if (!this.scene.textures.exists(DROP_KEY)) {
      const canvas = this.scene.textures.createCanvas(DROP_KEY, 1, 4) as Phaser.Textures.CanvasTexture;
      canvas.context.fillStyle = "rgba(112,176,236,1)";
      canvas.context.fillRect(0, 0, 1, 4);
      canvas.context.fillStyle = "rgba(226,244,255,1)";
      canvas.context.fillRect(0, 2, 1, 2);
      canvas.refresh();
    }
    if (!this.scene.textures.exists(SPLASH_KEY)) {
      const splash = this.scene.textures.createCanvas(SPLASH_KEY, 5, 2) as Phaser.Textures.CanvasTexture;
      splash.context.fillStyle = "rgba(196,226,246,1)";
      splash.context.fillRect(0, 1, 1, 1);
      splash.context.fillRect(4, 1, 1, 1);
      splash.context.fillRect(2, 0, 1, 1);
      splash.context.fillStyle = "rgba(150,196,236,1)";
      splash.context.fillRect(1, 1, 3, 1);
      splash.refresh();
    }
  }

  /**
   * How many drops fall or splash now, for the browser suite.
   *
   * @returns The count.
   */
  public get dropsFalling(): number {
    let count = 0;
    for (const drop of this.drops) if (drop.live) count += 1;
    return count;
  }

  /**
   * How many drip puddles hold water now, for the capture.
   *
   * @returns The count.
   */
  public get puddlesShown(): number {
    let count = 0;
    for (const slot of this.puddles) if (slot.live) count += 1;
    return count;
  }

  /**
   * Plays a frame: drops for each wet figure, its puddle grown while it stands still, every other puddle dried.
   *
   * @param now The time in ms.
   * @param detail The effects detail.
   */
  public update(now: number, detail: EffectsDetail): void {
    const dt = this.lastNow < 0 ? 0 : Math.max(0, Math.min(250, now - this.lastNow));
    this.lastNow = now;
    this.dt = dt;
    this.now = now;
    this.detail = detail;
    for (const slot of this.puddles) slot.feeding = false;
    this.tracks.forEach(this.unmark);
    this.figures(this.visit);
    this.tracks.forEach(this.sweep);
    for (const slot of this.puddles) this.growOrDry(slot, dt);
    for (const drop of this.drops) if (drop.live) this.fall(drop);
  }

  /**
   * Destroys the images and textures.
   */
  public destroy(): void {
    for (const drop of this.drops) {
      drop.image.destroy();
      drop.splash.destroy();
    }
    for (const slot of this.puddles) {
      slot.image.destroy();
      slot.texture.destroy();
    }
    this.drops.length = 0;
    this.puddles.length = 0;
    this.tracks.clear();
  }

  /**
   * Marks a track unseen at the start of a frame.
   *
   * @param track The track.
   */
  private readonly unmark = (track: Track): void => {
    track.seen = false;
  };

  /**
   * Forgets a figure no longer drawn; its puddle dries where it lies.
   *
   * @param track The track.
   * @param id The figure's id.
   */
  private readonly sweep = (track: Track, id: number): void => {
    if (!track.seen) this.tracks.delete(id);
  };

  /**
   * Visits one figure: drops while it is wet, and a puddle once it has stood still long enough.
   *
   * @param id The figure's id.
   * @param figure The figure.
   */
  private readonly visit: FigureVisit = (id, figure: CharacterFigure) => {
    const target = this.level(id);
    let track = this.tracks.get(id);
    if (track === undefined) {
      track = { x: figure.x, y: figure.y, stillMs: 0, nextDrop: this.now, puddle: null, seen: true, wet: target, noRoom: false };
      this.tracks.set(id, track);
    }
    track.seen = true;
    track.wet = target >= track.wet ? target : Math.max(target, track.wet - this.dt / DRIP_FADE_MS);
    const level = track.wet;
    const moved = Math.hypot(figure.x - track.x, figure.y - track.y);
    track.x = figure.x;
    track.y = figure.y;
    if (level <= 0 || this.rainingOn(figure.x, figure.y)) {
      track.stillMs = 0;
      track.puddle = null;
      return;
    }
    if (moved > STILL_PX) {
      track.stillMs = 0;
      track.puddle = null;
      track.noRoom = false;
    } else {
      track.stillMs += this.dt;
    }
    if (this.detail !== "off" && this.now >= track.nextDrop) {
      const gap = (DRIP_INTERVAL_MS / Math.max(0.15, level)) * (this.detail === "reduced" ? 2 : 1);
      track.nextDrop = this.now + gap * (0.6 + 0.8 * this.jitter(id, 1));
      this.startDrop(figure, id, level);
    }
    if (track.stillMs >= DRIP_PUDDLE_ONSET_MS && !track.noRoom) {
      if (track.puddle === null || track.puddle.owner !== id || !track.puddle.live) track.puddle = this.startPuddle(id, figure.x, figure.y + 2);
      if (track.puddle !== null) track.puddle.feeding = true;
      else track.noRoom = true;
    }
  };

  /**
   * A hashed number from 0 to 1 that moves with every call.
   *
   * @param id The figure's id.
   * @param salt A salt.
   * @returns The number.
   */
  private jitter(id: number, salt: number): number {
    this.serial = (this.serial + 1) >>> 0;
    return (Math.imul((id + 1) ^ Math.imul(this.serial, 0x9e3779b1), 0x85ebca6b ^ salt) >>> 8) / 0x1000000;
  }

  /**
   * Starts one drop at a figure's hem, in the next place of the pool.
   *
   * @param figure The figure.
   * @param id Its id.
   * @param level How wet it is.
   */
  private startDrop(figure: CharacterFigure, id: number, level: number): void {
    let drop = this.drops[this.nextDrop];
    if (drop === undefined) {
      const image = this.scene.add.image(0, 0, DROP_KEY).setOrigin(0.5, 1).setVisible(false);
      const splash = this.scene.add.image(0, 0, SPLASH_KEY).setOrigin(0.5, 1).setDepth(this.groundDepth + 0.00002).setVisible(false);
      drop = { image, splash, x: 0, top: 0, bottom: 0, startedAt: 0, live: false };
      this.drops.push(drop);
    }
    this.nextDrop = (this.nextDrop + 1) % DROP_POOL;
    const side = this.jitter(id, 2) - 0.5;
    drop.x = Math.round(figure.x + side * 14);
    drop.bottom = Math.round(figure.y - 1 + this.jitter(id, 3) * 3);
    drop.top = drop.bottom - 7 - Math.round(this.jitter(id, 4) * 6);
    drop.startedAt = this.now;
    drop.live = true;
    drop.image.setDepth(figure.depth + 0.00001).setAlpha(0.6 + 0.4 * level).setPosition(drop.x, drop.top).setVisible(true);
    drop.splash.setVisible(false);
  }

  /**
   * Moves a drop down to the ground, then shows its splash fading.
   *
   * @param drop The drop.
   */
  private fall(drop: Drop): void {
    const age = this.now - drop.startedAt;
    if (age < DRIP_FALL_MS) {
      const t = age / DRIP_FALL_MS;
      drop.image.setY(Math.round(drop.top + (drop.bottom - drop.top) * t * t));
      return;
    }
    drop.image.setVisible(false);
    const splashAge = age - DRIP_FALL_MS;
    if (splashAge >= DRIP_SPLASH_MS) {
      drop.live = false;
      drop.splash.setVisible(false);
      return;
    }
    drop.splash.setPosition(drop.x, drop.bottom).setAlpha(DRIP_SPLASH_ALPHA * (1 - splashAge / DRIP_SPLASH_MS)).setVisible(true);
  }

  /**
   * Starts a drip puddle under a figure: a spare slot, else the figure's own oldest past its cap, else none past the page's
   * cap; none either where even its smallest size would cross a blocked point (`dripGrowthThatFits`), and otherwise it
   * grows no larger than fits.
   *
   * @param id The figure's id.
   * @param x The feet's x.
   * @param y The feet's y.
   * @returns The puddle, or null when the page holds its most or there is no room.
   */
  private startPuddle(id: number, x: number, y: number): DripPuddle | null {
    let owned = 0;
    let oldest: DripPuddle | null = null;
    for (const slot of this.puddles) {
      if (!slot.live || slot.owner !== id) continue;
      owned += 1;
      if (oldest === null || slot.fill < oldest.fill) oldest = slot;
    }
    let slot: DripPuddle | null = owned >= DRIP_PUDDLES_PER_FIGURE ? oldest : (this.puddles.find((spare) => !spare.live) ?? null);
    if (slot === null && this.puddles.length < DRIP_PUDDLES_PER_VIEW) slot = this.makeSlot();
    if (slot === null) return null;
    const px = Math.round(x);
    const py = Math.round(y);
    const serial = (this.serial + 1) >>> 0;
    const most = dripGrowthThatFits((dx, dy) => this.blockedAt(px + dx, py + dy), serial, 1);
    if (most <= 0) return null;
    this.serial = serial;
    slot.serial = serial;
    slot.most = most;
    slot.x = px;
    slot.y = py;
    slot.live = true;
    slot.owner = id;
    slot.fill = 0;
    slot.step = -1;
    slot.image.setPosition(slot.x - SLOT_PX / 2, slot.y - SLOT_PX / 2).setVisible(false);
    return slot;
  }

  /**
   * Makes a pooled puddle slot.
   *
   * @returns The slot.
   */
  private makeSlot(): DripPuddle {
    const key = `weather:drip-puddle:${this.puddles.length}`;
    const texture = this.scene.textures.createCanvas(key, SLOT_PX, SLOT_PX) as Phaser.Textures.CanvasTexture;
    const image = this.scene.add.image(0, 0, key).setOrigin(0, 0).setDepth(this.groundDepth).setVisible(false);
    const slot: DripPuddle = { texture, image, pixels: new ImageData(SLOT_PX, SLOT_PX), live: false, serial: 0, x: 0, y: 0, owner: -1, fill: 0, step: -1, feeding: false, most: 1 };
    this.puddles.push(slot);
    return slot;
  }

  /**
   * Grows a puddle being dripped on, dries one that is not, and repaints it when its fill moves a step.
   *
   * @param slot The slot.
   * @param dt The frame's ms.
   */
  private growOrDry(slot: DripPuddle, dt: number): void {
    if (!slot.live) return;
    const level = this.tracks.get(slot.owner)?.wet ?? 0;
    slot.fill = slot.feeding ? Math.min(DRIP_PUDDLE_MAX_FILL * Math.max(0.4, level), slot.fill + dt / DRIP_PUDDLE_GROW_MS) : slot.fill - dt / DRIP_PUDDLE_DRY_MS;
    if (slot.fill <= 0) {
      slot.live = false;
      slot.image.setVisible(false);
      return;
    }
    const step = Math.round(slot.fill * FILL_STEPS);
    if (step === slot.step) return;
    slot.step = step;
    paintDripInto(slot.pixels, slot.serial, Math.min(slot.most, step / FILL_STEPS / DRIP_PUDDLE_MAX_FILL));
    slot.texture.context.clearRect(0, 0, SLOT_PX, SLOT_PX);
    slot.texture.context.putImageData(slot.pixels, 0, 0);
    slot.texture.refresh();
    slot.image.setVisible(step > 0);
  }
}
